/*
 * Free Gift Promotion System: cart reconciler.
 *
 * Authority:
 *  - The Shopify automatic Buy X Get Y discount decides the real $0 price (server side).
 *  - sections/gwp-state.liquid decides which gifts are owed (server side, from the live cart).
 *  - This script only applies that answer: it adds, fixes or removes gift lines.
 *
 * Properties:
 *  - Event driven (cart-update, cart page load, cart icon click). No polling.
 *  - Serialized: one run at a time, re-run once if something changed meanwhile, so quick
 *    consecutive cart changes can never produce duplicate gifts.
 *  - Idempotent: every pass re-reads the server state and only corrects the difference.
 *  - Fails safe: on any error the cart is left as it is. A gift line is never more than a
 *    normal line; without the discount it is priced normally, so no invalid free order exists.
 *  - Self-protecting: if a gift line is not actually priced at $0 (discount missing/expired/limit),
 *    the gift is removed and that promotion is paused for this browser session.
 */
(function () {
  'use strict';

  if (window.__gwpReconciler) return;
  window.__gwpReconciler = true;

  var TAG = '_gwp_promo';
  var STATE_SECTION = 'gwp-state';
  var MAX_PASSES = 3;
  var SUPPRESS_KEY = 'gwp_suppressed';
  var SUPPRESS_MS = 30 * 60 * 1000;

  var running = null;
  var queued = false;
  var queuedRefresh = false;
  var timer = null;
  var timerActive = false;
  var announced = {};

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  /* ---------- analytics (GTM dataLayer) ---------- */
  function track(name, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: name };
      for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
      window.dataLayer.push(payload);
    } catch (e) {}
  }

  /* ---------- per-session pause of a misbehaving promotion ---------- */
  function readSuppressed() {
    var out = {};
    try {
      var raw = JSON.parse(window.sessionStorage.getItem(SUPPRESS_KEY) || '{}');
      var now = Date.now();
      Object.keys(raw).forEach(function (id) {
        if (now - raw[id] < SUPPRESS_MS) out[id] = raw[id];
      });
    } catch (e) {}
    return out;
  }

  function suppress(id) {
    try {
      var cur = readSuppressed();
      cur[id] = Date.now();
      window.sessionStorage.setItem(SUPPRESS_KEY, JSON.stringify(cur));
    } catch (e) {}
  }

  /* ---------- network ---------- */
  function post(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok || json.status) {
          var err = new Error(json.description || json.message || 'cart request failed');
          err.payload = json;
          throw err;
        }
        return json;
      });
    });
  }

  // The Section Rendering API accepts at most 5 sections per request, so ask in batches of 5.
  function getSections(names) {
    var chunks = [];
    for (var i = 0; i < names.length; i += 5) chunks.push(names.slice(i, i + 5));

    return Promise.all(chunks.map(function (chunk) {
      return fetch(root() + '?sections=' + encodeURIComponent(chunk.join(',')) + '&_=' + Date.now(), {
        headers: { 'Accept': 'application/json' },
        cache: 'no-store'
      }).then(function (response) {
        if (!response.ok) throw new Error('sections ' + response.status);
        return response.json();
      });
    })).then(function (parts) {
      var merged = {};
      parts.forEach(function (part) {
        for (var key in part) if (Object.prototype.hasOwnProperty.call(part, key)) merged[key] = part[key];
      });
      return merged;
    });
  }

  function getState() {
    return getSections([STATE_SECTION]).then(function (sections) {
      var doc = new DOMParser().parseFromString(sections[STATE_SECTION] || '', 'text/html');
      var node = doc.getElementById('gwp-state-json');
      if (!node) throw new Error('gwp state missing');
      return JSON.parse(node.textContent);
    });
  }

  /* ---------- planning ---------- */

  // Shopify splits one cart line into several display lines when a discount only covers part of
  // its quantity (e.g. 4 paid + 1 free, same key). Treat lines that share a key as one line.
  function mergeByKey(lines) {
    var byKey = {};
    var order = [];
    lines.forEach(function (l) {
      var m = byKey[l.key];
      if (!m) {
        m = byKey[l.key] = {
          key: l.key, variant_id: l.variant_id, promo: l.promo, quantity: 0,
          original_line_price: 0, final_line_price: 0
        };
        order.push(m);
      }
      m.quantity += l.quantity;
      m.original_line_price += l.original_line_price;
      m.final_line_price += l.final_line_price;
    });
    return order;
  }

  function plan(state, suppressed) {
    var removals = [];
    var sets = [];
    var adds = [];
    var promos = {};
    var tagged = {};

    state.promos.forEach(function (p) { promos[p.id] = p; });
    mergeByKey(state.lines).forEach(function (line) {
      if (line.promo) (tagged[line.promo] = tagged[line.promo] || []).push(line);
    });

    // Gift lines that already exist: keep exactly the owed quantity, drop anything that does not belong.
    var have = {};
    Object.keys(tagged).forEach(function (id) {
      var p = promos[id];
      var owed = p && !suppressed[id] ? p.desired_qty : 0;
      var good = [];

      tagged[id].forEach(function (line) {
        if (p && line.variant_id === p.gift_variant_id) {
          good.push(line);
        } else {
          removals.push({ key: line.key, quantity: 0, promo: id });
        }
      });

      var total = good.reduce(function (sum, l) { return sum + l.quantity; }, 0);
      have[id] = total;

      var excess = total - owed;
      for (var i = good.length - 1; i >= 0 && excess > 0; i--) {
        var cut = Math.min(excess, good[i].quantity);
        var want = good[i].quantity - cut;
        (want === 0 ? removals : sets).push({ key: good[i].key, quantity: want, promo: id });
        excess -= cut;
      }
    });

    // Gifts that are owed but missing (all or part): add the difference as a gift line.
    state.promos.forEach(function (p) {
      if (suppressed[p.id] || p.desired_qty < 1) return;
      var current = have[p.id] || 0;
      if (current < p.desired_qty) {
        adds.push({ variantId: p.gift_variant_id, quantity: p.desired_qty - current, promo: p.id });
      }
    });

    return { removals: removals, sets: sets, adds: adds };
  }

  /*
   * Did Shopify really make the owed gift units free? Looks at every line of the gift variant
   * (gift lines and the customer's own paid lines alike), because the discount may be allocated
   * to any unit of that variant. Only judged once the gift line holds the full owed quantity.
   */
  function findUnpriced(state, suppressed) {
    var bad = [];
    var lines = mergeByKey(state.lines);

    state.promos.forEach(function (p) {
      if (suppressed[p.id] || p.desired_qty < 1) return;

      var giftQty = 0;
      var units = 0;
      var original = 0;
      var final = 0;
      lines.forEach(function (l) {
        if (l.variant_id !== p.gift_variant_id) return;
        units += l.quantity;
        original += l.original_line_price;
        final += l.final_line_price;
        if (l.promo === p.id) giftQty += l.quantity;
      });

      if (giftQty < p.desired_qty || units < 1) return;
      var unitPrice = original / units;
      var freeValue = original - final;
      // Allow one cent of rounding slack.
      if (freeValue + 1 < unitPrice * p.desired_qty) bad.push(p.id);
    });

    return bad;
  }

  /* ---------- applying ---------- */
  function applyOps(ops, events) {
    var chain = Promise.resolve();
    var changed = false;

    ops.removals.concat(ops.sets).forEach(function (op) {
      chain = chain.then(function () {
        return post('cart/change.js', { id: op.key, quantity: op.quantity }).then(function () {
          changed = true;
          events.push({
            name: op.quantity === 0 ? 'gwp_gift_removed' : 'gwp_gift_updated',
            promo: op.promo, quantity: op.quantity
          });
        });
      });
    });

    ops.adds.forEach(function (op) {
      chain = chain.then(function () {
        var props = {};
        props[TAG] = op.promo;
        var request = post('cart/add.js', { id: op.variantId, quantity: op.quantity, properties: props }).then(function () {
          changed = true;
          events.push({ name: 'gwp_gift_added', promo: op.promo, quantity: op.quantity });
        });
        return request.catch(function (error) {
          // Could not add the gift (e.g. sold out in the meantime): stop offering it this session.
          suppress(op.promo);
          track('gwp_error', { promo_id: op.promo, message: String(error && error.message) });
          throw error;
        });
      });
    });

    return chain.then(function () { return changed; });
  }

  /* ---------- keeping the visible cart in sync ---------- */
  var CART_TAGS = ['cart-items', 'cart-drawer-items', 'cart-popup-items', 'cart-notification-items'];

  function collectComponents() {
    var found = [];
    CART_TAGS.forEach(function (tag) {
      Array.prototype.forEach.call(document.querySelectorAll(tag), function (el) {
        if (typeof el.getSectionsToRender !== 'function') return;
        try { found.push({ el: el, list: el.getSectionsToRender() }); } catch (e) {}
      });
    });
    return found;
  }

  function refreshUi(itemCount) {
    var comps = collectComponents();
    var names = [];
    comps.forEach(function (c) {
      c.list.forEach(function (s) { if (s.section && names.indexOf(s.section) === -1) names.push(s.section); });
    });
    if (!names.length) return Promise.resolve();

    return getSections(names).then(function (sections) {
      comps.forEach(function (c) {
        c.list.forEach(function (s) {
          try {
            var html = sections[s.section];
            var host = document.getElementById(s.id);
            if (!html || !host) return;
            var target = host.querySelector(s.selector) || host;
            var source = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector);
            if (source) target.innerHTML = source.innerHTML;
          } catch (e) {}
        });
      });

      var empty = itemCount === 0;
      comps.forEach(function (c) { c.el.classList.toggle('is-empty', empty); });
      ['main-cart-footer'].forEach(function (id) {
        var node = document.getElementById(id);
        if (node) node.classList.toggle('is-empty', empty);
      });
      ['cart-drawer', 'cart-popup', 'cart-notification'].forEach(function (tag) {
        var node = document.querySelector(tag);
        if (node) node.classList.toggle('is-empty', empty);
      });
    });
  }

  /* ---------- the run loop ---------- */
  function countItems(state) {
    return state.lines.reduce(function (sum, l) { return sum + l.quantity; }, 0);
  }

  function announce(state) {
    state.promos.forEach(function (p) {
      if (p.desired_qty > 0 && !announced[p.id]) {
        announced[p.id] = true;
        track('gwp_promo_triggered', {
          promo_id: p.id, gift_sku: p.gift_sku, merchant_item_id: p.merchant_item_id, quantity: p.desired_qty
        });
      }
    });
  }

  function runOnce(forceRefresh) {
    var anyChange = false;
    var lastState = null;

    function pass(n) {
      return getState().then(function (state) {
        lastState = state;
        announce(state);
        var suppressed = readSuppressed();

        // Did Shopify actually price our gift lines at $0?
        findUnpriced(state, suppressed).forEach(function (id) {
          suppress(id);
          suppressed[id] = Date.now();
          track('gwp_discount_not_applied', { promo_id: id });
        });

        var ops = plan(state, suppressed);
        var work = ops.removals.length + ops.sets.length + ops.adds.length;
        if (!work || n >= MAX_PASSES) return null;

        var events = [];
        return applyOps(ops, events).then(function (changed) {
          anyChange = anyChange || changed;
          events.forEach(function (ev) {
            var promo = null;
            state.promos.forEach(function (p) { if (p.id === ev.promo) promo = p; });
            track(ev.name, {
              promo_id: ev.promo,
              quantity: ev.quantity,
              gift_sku: promo ? promo.gift_sku : '',
              merchant_item_id: promo ? promo.merchant_item_id : ''
            });
          });
          return pass(n + 1);
        });
      });
    }

    return pass(1).then(function () {
      if (!anyChange && !forceRefresh) return;
      // The last pass read the final cart state, so reuse it for the item count.
      return refreshUi(lastState ? countItems(lastState) : null);
    });
  }

  function reconcile(forceRefresh) {
    if (running) {
      queued = true;
      queuedRefresh = queuedRefresh || !!forceRefresh;
      return running;
    }
    running = runOnce(forceRefresh)
      .catch(function () { /* fail safe: leave the cart untouched */ })
      .then(function () {
        running = null;
        if (queued) {
          var again = queuedRefresh;
          queued = false;
          queuedRefresh = false;
          return reconcile(again);
        }
      });
    return running;
  }

  function schedule(forceRefresh) {
    window.clearTimeout(timer);
    timerActive = true;
    timer = window.setTimeout(function () {
      timerActive = false;
      reconcile(forceRefresh);
    }, 150);
  }

  // Runs the callback once no correction is running or waiting to start.
  function whenIdle(callback) {
    (function check() {
      if (running || timerActive) {
        window.setTimeout(check, 50);
      } else {
        callback();
      }
    })();
  }

  /* ---------- triggers ---------- */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'gwp') return;
      schedule(false);
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    // Opening the cart: make sure it is current (promotion may have ended, stock may have changed).
    if (target.closest('#cart-icon-bubble')) schedule(true);

    // Never let checkout start while a gift correction is still in flight.
    var checkout = target.closest('[name="checkout"], a[href*="/checkout"]');
    if (checkout && (running || timerActive) && !checkout.hasAttribute('data-gwp-released')) {
      event.preventDefault();
      event.stopPropagation();
      whenIdle(function () {
        // The cart refresh may have replaced the button, so use the one that is on the page now.
        var fresh = document.body.contains(checkout) ? checkout : document.querySelector('[name="checkout"]');
        if (fresh) {
          fresh.setAttribute('data-gwp-released', 'true');
          fresh.click();
        } else {
          window.location.href = root() + 'checkout';
        }
      });
    }
  }, true);

  // Quick add modal re-renders the notification after it closes; re-sync afterwards.
  document.body && document.body.addEventListener('modalClosed', function () {
    window.setTimeout(function () { reconcile(true); }, 450);
  });

  window.addEventListener('pageshow', function (event) {
    if (isCartPage() && event.persisted) schedule(true);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible' && isCartPage()) schedule(false);
  });

  if (isCartPage()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { schedule(false); });
    } else {
      schedule(false);
    }
  }

  // Small public hook for testing and support.
  window.GWP = { reconcile: function () { return reconcile(true); } };
})();
